import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RotateCcw, ShieldAlert, Video } from "lucide-react";
import { LaksmiHero, LaksmiInfo, LaksmiJoinForm, LaksmiStatusOverlay, useGuestChime } from "@/components/laksmi/LaksmiShared";
import "@/pages/laksmi/Laksmi.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LaksmiPublicPage() {
  const [state, setState] = useState(null);
  const [ref, setRefState] = useState(() => { try { return JSON.parse(sessionStorage.getItem("laksmi_pub_ref") || "null"); } catch (_) { return null; } });
  const setRef = (v) => { setRefState(v); if (v) sessionStorage.setItem("laksmi_pub_ref", JSON.stringify(v)); else sessionStorage.removeItem("laksmi_pub_ref"); };
  const [status, setStatus] = useState(null);
  const [formInit, setFormInit] = useState(() => { try { return JSON.parse(sessionStorage.getItem("laksmi_pub_form") || "null"); } catch (_) { return null; } });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadState = useCallback(async () => {
    try { const res = await axios.get(`${API}/laksmi/public/state`); setState(res.data); } catch (_) {}
  }, []);
  useEffect(() => { loadState(); const t = setInterval(loadState, 15000); return () => clearInterval(t); }, [loadState]);

  useEffect(() => {
    if (!ref) return undefined;
    const poll = async () => {
      try { const res = await axios.get(`${API}/laksmi/public/status`, { params: ref }); setStatus(res.data); } catch (_) {}
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [ref]);

  // Overlay "selesai" auto-hides after 30 seconds, returning to the landing page.
  useEffect(() => {
    if (status?.code !== "selesai") return undefined;
    const t = setTimeout(() => { setStatus(null); setRef(null); sessionStorage.removeItem("laksmi_pub_form"); }, 30000);
    return () => clearTimeout(t);
  }, [status?.code]);

  const { showDismiss, dismiss } = useGuestChime(status?.code);

  const submit = async (fields) => {
    setBusy(true); setError("");
    try {
      const res = await axios.post(`${API}/laksmi/public/submit`, fields);
      sessionStorage.setItem("laksmi_pub_form", JSON.stringify(fields));
      setFormInit(fields);
      setRef({ guest_id: res.data.guest_id, token: res.data.token });
    } catch (err) { setError(err.response?.data?.detail || "Gagal mengirim data."); }
    finally { setBusy(false); }
  };

  const fixForm = () => { setStatus(null); setRef(null); };
  const checkLink = useCallback(async (url) => {
    const res = await axios.post(`${API}/laksmi/public/check-link`, { url });
    return res.data;
  }, []);

  const join = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/laksmi/public/join`, ref);
      if (res.data.zoom_link) window.open(res.data.zoom_link, "_blank", "noopener");
    } catch (err) { setError(err.response?.data?.detail || "Gagal join."); }
    finally { setBusy(false); }
  };

  const renderBody = () => {
    if (!ref) {
      return state?.mode === "open"
        ? <LaksmiJoinForm isLogin={false} submitLabel="DAFTAR LAKSMI HARI INI" busy={busy} error={error} onSubmit={submit} initial={formInit} onCheckLink={checkLink} />
        : <LaksmiInfo mode={state?.mode || "regular"} message={state?.message || ""} />;
    }
    const code = status?.code;
    if (status?.in_room || code === "selesai") return <LaksmiStatusOverlay status={status} showDismiss={showDismiss} onDismiss={dismiss} />;
    if (code === "ditolak") return (
      <div className="laksmi-verify laksmi-verify-reject" data-testid="laksmi-rejected">
        <ShieldAlert size={30} /><p>Dokumen Anda tidak valid</p>
        {status?.reason ? <p className="laksmi-reject-reason" data-testid="laksmi-reject-reason-text">Catatan petugas: {status.reason}</p> : null}
        <button className="laksmi-primary" data-testid="laksmi-fix-form" onClick={fixForm}><RotateCcw size={15} /> Perbaiki Formulir</button>
      </div>
    );
    if (code === "siap_join") return (
      <div className="laksmi-verify laksmi-verify-ready" data-testid="laksmi-ready">
        <p className="laksmi-verify-title">SILAKAN JOIN ZOOM di bawah ini, pastikan Anda memakai nama <strong>{status?.nama_zoom}</strong> di dalam Zoom Meeting</p>
        <button className="laksmi-primary" data-testid="laksmi-public-join" disabled={busy} onClick={join}>{busy ? <Loader2 size={16} className="laksmi-spin" /> : <Video size={16} />} JOIN ZOOM LAKSMI</button>
      </div>
    );
    // pending verification (default)
    return <div className="laksmi-verify laksmi-verify-wait" data-testid="laksmi-waiting-verify"><Loader2 size={30} className="laksmi-spin" /><p>Mohon Menunggu Verifikasi Petugas,<br /><strong>JANGAN TINGGALKAN HALAMAN INI</strong></p></div>;
  };

  const overlayOnly = ref && (status?.in_room || status?.code === "selesai");

  return (
    <div className="laksmi-public" data-testid="laksmi-public-page">
      <header className="laksmi-public-head"><img src="/logo-padma-transparent.png" alt="Logo PADMA" /><div><strong>LAKSMI</strong><span>LLDIKTI Wilayah VII</span></div></header>
      <main className="laksmi-page">
        {overlayOnly ? renderBody() : <LaksmiHero>{renderBody()}</LaksmiHero>}
      </main>
    </div>
  );
}
